/*
  Ride More – teeman yhteinen JavaScript (ei riippuvuuksia).
  Shopify-rajapinnat: AJAX Cart API (/cart/add.js, /cart/change.js, /cart.js), Section Rendering API
  (?sections= ja ?section_id=), Predictive Search (/search/suggest).
*/
(() => {
  const theme = window.theme || {};
  const $ = (sel, root = document) => root.querySelector(sel);
  const $$ = (sel, root = document) => [...root.querySelectorAll(sel)];
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  /* ------------------------------------------------------------ apurit */
  const debounce = (fn, ms) => {
    let t;
    return (...a) => {
      clearTimeout(t);
      t = setTimeout(() => fn(...a), ms);
    };
  };
  const focusables = (root) =>
    $$('a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"]), summary', root).filter(
      (el) => el.offsetParent !== null || el === document.activeElement,
    );

  let lockCount = 0;
  const lockScroll = (on) => {
    lockCount = Math.max(0, lockCount + (on ? 1 : -1));
    document.documentElement.classList.toggle('is-locked', lockCount > 0);
  };

  function trapFocus(container, event) {
    if (event.key !== 'Tab') return;
    const items = focusables(container);
    if (!items.length) return;
    const first = items[0];
    const last = items[items.length - 1];
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  }

  function toast(message, type = 'info') {
    const el = $('[data-toast]');
    if (!el) return;
    el.textContent = message;
    el.dataset.type = type;
    el.classList.add('is-visible');
    clearTimeout(el._t);
    el._t = setTimeout(() => el.classList.remove('is-visible'), 3500);
  }
  window.themeToast = toast;

  /* ------------------------------------------------------------ kiinteä ylätunniste */
  class StickyHeader extends HTMLElement {
    connectedCallback() {
      if (this.dataset.sticky !== 'true') return;
      const sentinel = document.createElement('div');
      sentinel.style.cssText = 'position:absolute;top:0;height:1px;width:1px;';
      document.body.prepend(sentinel);
      new IntersectionObserver(([e]) => this.classList.toggle('is-scrolled', !e.isIntersecting)).observe(sentinel);
      // Ylätunnisteen korkeus muuttujaksi (ankkurit, kiinteät paneelit)
      const setH = () => document.documentElement.style.setProperty('--header-total', `${this.offsetHeight}px`);
      setH();
      new ResizeObserver(setH).observe(this);
    }
  }
  customElements.define('sticky-header', StickyHeader);

  /* ------------------------------------------------------------ iso valikko */
  class HeaderMenu extends HTMLElement {
    connectedCallback() {
      this.triggers = $$('button.mega__trigger', this);
      this.hoverable = window.matchMedia('(hover: hover) and (pointer: fine)');
      this.triggers.forEach((btn) => {
        const panel = document.getElementById(btn.getAttribute('aria-controls'));
        const item = btn.closest('.mega__item');
        btn.addEventListener('click', () => this.toggle(btn, panel));
        item.addEventListener('pointerenter', () => {
          if (!this.hoverable.matches) return;
          clearTimeout(this.closeTimer);
          this.openTimer = setTimeout(() => this.open(btn, panel), 90);
        });
        item.addEventListener('pointerleave', () => {
          if (!this.hoverable.matches) return;
          clearTimeout(this.openTimer);
          this.closeTimer = setTimeout(() => this.close(btn, panel), 160);
        });
        item.addEventListener('focusout', (e) => {
          if (!item.contains(e.relatedTarget)) this.close(btn, panel);
        });
      });
      document.addEventListener('keydown', (e) => {
        if (e.key !== 'Escape') return;
        const open = this.triggers.find((b) => b.getAttribute('aria-expanded') === 'true');
        if (open) {
          this.close(open, document.getElementById(open.getAttribute('aria-controls')));
          open.focus();
        }
      });
      document.addEventListener('click', (e) => {
        if (!this.contains(e.target)) this.closeAll();
      });
    }
    open(btn, panel) {
      this.triggers.forEach((b) => b !== btn && this.close(b, document.getElementById(b.getAttribute('aria-controls'))));
      btn.setAttribute('aria-expanded', 'true');
      panel.classList.add('is-open');
    }
    close(btn, panel) {
      btn.setAttribute('aria-expanded', 'false');
      if (panel) panel.classList.remove('is-open');
    }
    closeAll() {
      this.triggers.forEach((b) => this.close(b, document.getElementById(b.getAttribute('aria-controls'))));
    }
    toggle(btn, panel) {
      btn.getAttribute('aria-expanded') === 'true' ? this.close(btn, panel) : this.open(btn, panel);
    }
  }
  customElements.define('header-menu', HeaderMenu);

  /* ------------------------------------------------------------ laatikko (valikko, ostoskori, suodattimet) */
  class DrawerPanel extends HTMLElement {
    connectedCallback() {
      this.panel = $('.drawer__panel', this);
      this.addEventListener('click', (e) => {
        if (e.target.closest('[data-drawer-close]')) this.close();
      });
      this.addEventListener('keydown', (e) => {
        if (e.key === 'Escape') this.close();
        trapFocus(this.panel, e);
      });
      document.addEventListener('click', (e) => {
        const opener = e.target.closest(`[data-drawer-open="${this.id}"]`);
        if (opener) {
          e.preventDefault();
          this.open(opener);
        }
      });
    }
    open(opener) {
      if (this.hasAttribute('open')) return;
      this.opener = opener || document.activeElement;
      clearTimeout(this.hideTimer);
      this.hidden = false;
      void this.offsetWidth; // siirtymä alkaa näkyvästä tilasta
      this.setAttribute('open', '');
      this.panel.setAttribute('role', 'dialog');
      this.panel.setAttribute('aria-modal', 'true');
      if (this.opener?.setAttribute) this.opener.setAttribute('aria-expanded', 'true');
      lockScroll(true);
      requestAnimationFrame(() => (focusables(this.panel)[0] || this.panel).focus());
    }
    close() {
      if (!this.hasAttribute('open')) return;
      this.removeAttribute('open');
      // Suljettu laatikko piilotetaan kokonaan siirtymän jälkeen (ei kohdistettavissa, ei ruudunlukijalle)
      if (!this.hasAttribute('data-desktop-static')) this.hideTimer = setTimeout(() => (this.hidden = true), 450);
      if (this.hasAttribute('data-desktop-static')) {
        this.panel.removeAttribute('role');
        this.panel.removeAttribute('aria-modal');
      }
      if (this.opener?.setAttribute) this.opener.setAttribute('aria-expanded', 'false');
      lockScroll(false);
      this.opener?.focus?.();
    }
  }
  customElements.define('drawer-panel', DrawerPanel);
  // Linkki, joka toimii painikkeena (role="button"): välilyönti avaa kuten painike
  document.addEventListener('keydown', (e) => {
    const el = e.target.closest?.('a[role="button"]');
    if (el && (e.key === ' ' || e.key === 'Spacebar')) {
      e.preventDefault();
      el.click();
    }
  });

  /* ------------------------------------------------------------ haku */
  class PredictiveSearch extends HTMLElement {
    connectedCallback() {
      this.input = $('input[type="search"]', this);
      this.results = $('[data-search-results]', this);
      this.status = $('[data-search-status]', this);
      this.enabled = this.dataset.enabled === 'true';
      this.cache = new Map();
      document.addEventListener('click', (e) => {
        if (e.target.closest('[data-search-open]')) {
          e.preventDefault();
          this.open(e.target.closest('[data-search-open]'));
        }
      });
      this.addEventListener('click', (e) => {
        if (e.target.closest('[data-search-close]')) this.close();
      });
      this.addEventListener('keydown', (e) => {
        if (e.key === 'Escape') this.close();
        if (e.key === 'ArrowDown' || e.key === 'ArrowUp') this.move(e);
        if (e.key === 'Enter') {
          const sel = $('[aria-selected="true"] a', this.results);
          if (sel) {
            e.preventDefault();
            window.location.href = sel.href;
          }
        }
        trapFocus($('.search-dialog__panel', this), e);
      });
      if (this.enabled) this.input.addEventListener('input', debounce(() => this.search(), 220));
    }
    open(opener) {
      this.opener = opener;
      this.hidden = false;
      lockScroll(true);
      requestAnimationFrame(() => this.input.focus());
    }
    close() {
      if (this.hidden) return;
      this.hidden = true;
      lockScroll(false);
      this.input.setAttribute('aria-expanded', 'false');
      this.opener?.focus?.();
    }
    async search() {
      const q = this.input.value.trim();
      if (q.length < 2) {
        this.results.innerHTML = '';
        this.input.setAttribute('aria-expanded', 'false');
        return;
      }
      this.status.textContent = theme.strings?.loading || '';
      this.results.setAttribute('aria-busy', 'true');
      try {
        let html = this.cache.get(q);
        if (!html) {
          const params = new URLSearchParams({ q, 'resources[type]': 'product,collection,page,query', 'resources[limit]': '6', 'resources[options][unavailable_products]': 'last', section_id: 'predictive-search' });
          const res = await fetch(`${theme.routes.predictiveSearch}?${params}`);
          if (!res.ok) throw new Error(res.status);
          const doc = new DOMParser().parseFromString(await res.text(), 'text/html');
          html = $('.ps', doc)?.outerHTML || '';
          this.cache.set(q, html);
        }
        if (q !== this.input.value.trim()) return;
        this.results.innerHTML = html;
        this.input.setAttribute('aria-expanded', html ? 'true' : 'false');
        const count = $('[data-results-count]', this.results)?.dataset.resultsCount || '0';
        this.status.textContent = `${count}`;
      } catch {
        this.results.innerHTML = `<p class="notice notice--error">${theme.strings.cartError}</p>`;
      } finally {
        this.results.removeAttribute('aria-busy');
      }
    }
    move(e) {
      const options = $$('[role="option"]', this.results);
      if (!options.length) return;
      e.preventDefault();
      const i = options.findIndex((o) => o.getAttribute('aria-selected') === 'true');
      const next = e.key === 'ArrowDown' ? (i + 1) % options.length : (i - 1 + options.length) % options.length;
      options.forEach((o) => o.setAttribute('aria-selected', 'false'));
      options[next].setAttribute('aria-selected', 'true');
      this.input.setAttribute('aria-activedescendant', options[next].id);
      options[next].scrollIntoView({ block: 'nearest' });
    }
  }
  customElements.define('predictive-search', PredictiveSearch);

  /* ------------------------------------------------------------ ostoskori */
  const cartSections = () => $$('[data-cart-section]').map((el) => el.dataset.cartSection);

  async function cartRequest(url, body) {
    const sections = cartSections();
    const res = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ ...body, sections, sections_url: window.location.pathname }),
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok || data.status) {
      const err = new Error(data.description || data.message || theme.strings.cartError);
      err.data = data;
      throw err;
    }
    return data;
  }

  function renderCartSections(sections) {
    if (!sections) return;
    for (const [id, html] of Object.entries(sections)) {
      const target = $(`[data-cart-section="${id}"]`);
      if (!target || !html) continue;
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const fresh = $(`[data-cart-section="${id}"]`, doc);
      if (fresh) target.innerHTML = fresh.innerHTML;
    }
  }

  // Huoltovaraus on yksi rivi, vaikka sen määrä on varattu työaika (30 min jaksoina)
  const shownCount = (cart) => cart.items.reduce((n, i) => n + (i.product_type === 'Huoltovaraus' ? 1 : i.quantity), 0);
  async function refreshCount(cart) {
    if (!cart?.items) cart = await (await fetch(`${theme.routes.cart}.js`)).json();
    const count = shownCount(cart);
    $$('[data-cart-count]').forEach((el) => {
      el.textContent = count;
      el.classList.toggle('is-empty', count === 0);
      el.classList.remove('is-bumped');
      void el.offsetWidth;
      if (!reduceMotion.matches) el.classList.add('is-bumped');
    });
    $$('[data-cart-count-label]').forEach((el) => {
      el.textContent = el.textContent.replace(/\d+/, count);
    });
  }

  function openCartDrawer(opener) {
    const drawer = document.getElementById('CartDrawer');
    if (drawer && theme.cartType === 'drawer') drawer.open(opener);
    else window.location.href = theme.routes.cart;
  }
  document.addEventListener('click', (e) => {
    const opener = e.target.closest('[data-cart-open]');
    if (opener && document.getElementById('CartDrawer')) {
      e.preventDefault();
      openCartDrawer(opener);
    }
  });

  // Lisää ostoskoriin: kaikki [data-product-form]-lomakkeet (tuotesivu, pikalisäys).
  document.addEventListener('submit', async (e) => {
    const form = e.target.closest('form[data-product-form]');
    if (!form) return;
    e.preventDefault();
    // Kortin variantinvalinnassa jokainen nappi on oma lähetyspainikkeensa (name="id" value=variantti)
    const chosen = e.submitter?.name === 'id' ? e.submitter : null;
    const button = chosen || $('[data-add-button]', form) || $('[type="submit"]', form);
    if (button.getAttribute('aria-busy') === 'true' || button.disabled) return; // estää tuplalähetyksen
    const id = chosen ? chosen.value : form.querySelector('[name="id"]')?.value;
    if (!id) {
      toast(theme.strings.chooseOption, 'error');
      return;
    }
    const errorBox = form.querySelector('[data-form-error]');
    if (errorBox) errorBox.hidden = true;
    button.setAttribute('aria-busy', 'true');
    try {
      const fd = new FormData(form);
      // Rivin ominaisuudet (esim. lahjakortin saaja): kentät properties[…]; poissa käytöstä olevat eivät ole mukana.
      const properties = {};
      for (const [name, value] of fd) {
        const m = name.match(/^properties\[(.+)\]$/);
        if (m && value !== '') properties[m[1]] = value;
      }
      const item = { id: Number(id), quantity: Number(fd.get('quantity') || 1) };
      if (Object.keys(properties).length) item.properties = properties;
      const data = await cartRequest(theme.routes.cartAdd + '.js', { items: [item] });
      renderCartSections(data.sections);
      await refreshCount();
      document.dispatchEvent(new CustomEvent('cart:updated'));
      if (theme.cartType === 'drawer' && document.getElementById('CartDrawer')) openCartDrawer($('[data-quick-toggle]', form) || button);
      else toast(theme.strings.added, 'success');
    } catch (err) {
      const msg = err.message || theme.strings.cartError;
      if (errorBox) {
        errorBox.textContent = msg;
        errorBox.hidden = false;
      } else toast(msg, 'error');
    } finally {
      button.removeAttribute('aria-busy');
    }
  });

  // Ostoskorin rivit: määrän muutos ja poisto (laatikko ja sivu)
  async function changeLine(el, quantity) {
    const root = el.closest('[data-cart-root]');
    const line = el.closest('[data-line-key]');
    if (!line || root?.getAttribute('aria-busy') === 'true') return;
    root?.setAttribute('aria-busy', 'true');
    const err = root?.querySelector('[data-cart-error]');
    if (err) err.hidden = true;
    try {
      const data = await cartRequest(theme.routes.cartChange + '.js', { id: line.dataset.lineKey, quantity });
      renderCartSections(data.sections);
      await refreshCount(data);
      document.dispatchEvent(new CustomEvent('cart:updated'));
    } catch (e) {
      const fresh = root?.querySelector('[data-cart-error]');
      if (fresh) {
        fresh.textContent = e.message || theme.strings.quantityError;
        fresh.hidden = false;
      } else toast(e.message || theme.strings.cartError, 'error');
    } finally {
      root?.removeAttribute('aria-busy');
    }
  }
  document.addEventListener('click', (e) => {
    const remove = e.target.closest('[data-line-remove]');
    if (remove) {
      e.preventDefault();
      changeLine(remove, 0);
    }
  });
  document.addEventListener(
    'change',
    debounce((e) => {
      const input = e.target.closest('[data-line-quantity]');
      if (input) changeLine(input, Math.max(0, Number(input.value) || 0));
    }, 300),
  );
  // Tilauksen lisätiedot
  document.addEventListener(
    'input',
    debounce((e) => {
      const note = e.target.closest('[data-cart-note]');
      if (note) fetch(theme.routes.cartUpdate + '.js', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ note: note.value }) });
    }, 500),
  );

  /* ------------------------------------------------------------ määrävalitsin */
  class QuantityInput extends HTMLElement {
    connectedCallback() {
      this.input = $('input', this);
      this.addEventListener('click', (e) => {
        const btn = e.target.closest('button[data-step]');
        if (!btn) return;
        const step = Number(btn.dataset.step);
        const min = Number(this.input.min || 0);
        const max = this.input.max ? Number(this.input.max) : Infinity;
        const next = Math.min(max, Math.max(min, (Number(this.input.value) || 0) + step));
        if (next === Number(this.input.value)) return;
        this.input.value = next;
        this.input.dispatchEvent(new Event('change', { bubbles: true }));
        this.sync();
      });
      this.input.addEventListener('change', () => this.sync());
      this.sync();
    }
    sync() {
      const v = Number(this.input.value);
      const minus = $('button[data-step="-1"]', this);
      if (minus) minus.disabled = v <= Number(this.input.min || 0);
    }
  }
  customElements.define('quantity-input', QuantityInput);

  /* Merkkien logot: Näytä lisää / Näytä vähemmän (tiivistetty näkymä, sections/brand-logos.liquid) */
  class BrandLogos extends HTMLElement {
    connectedCallback() {
      const grid = $('.brand-logos__grid', this);
      const btn = $('.brand-logos__more button', this);
      if (!grid || !btn) return;
      // Painike vain, jos jokin logo on piilossa (vähän merkkejä = kaikki mahtuvat kahdelle riville)
      const sync = () => {
        const hidden = [...grid.children].some((li) => li.offsetParent === null);
        btn.parentElement.hidden = !grid.hasAttribute('data-collapsed') ? false : !hidden;
      };
      btn.addEventListener('click', () => {
        const open = grid.hasAttribute('data-collapsed');
        grid.toggleAttribute('data-collapsed', !open);
        btn.setAttribute('aria-expanded', String(open));
        btn.textContent = open ? btn.dataset.less : btn.dataset.more;
        if (!open) grid.scrollIntoView({ block: 'nearest' });
      });
      sync();
      window.addEventListener('resize', sync, { passive: true });
    }
  }
  customElements.define('brand-logos', BrandLogos);

  /* ------------------------------------------------------------ tuotekortin variantinvalinta (product-card.liquid) */
  // ”Lisää koriin” avaa valinnan; nappi lähettää lomakkeen kyseisellä variantilla (yllä oleva submit-käsittelijä).
  // Sulkeutuu Escillä, ulos klikatessa, toisen kortin valinnan avautuessa ja kun tuote on lisätty.
  class QuickAdd extends HTMLElement {
    connectedCallback() {
      this.toggle = $('[data-quick-toggle]', this);
      this.panel = $('.card__choices', this);
      this.toggle.addEventListener('click', () => (this.panel.hidden ? this.open() : this.close()));
      this.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && !this.panel.hidden) {
          e.stopPropagation();
          this.close(true);
        }
      });
      this.onOutside = (e) => {
        if (!this.contains(e.target)) this.close();
      };
      this.onAdded = () => this.close();
    }
    open() {
      document.dispatchEvent(new CustomEvent('quickadd:open', { detail: this }));
      this.panel.hidden = false;
      this.toggle.setAttribute('aria-expanded', 'true');
      this.closest('.card')?.classList.add('card--choosing');
      $('.card__choice:not(:disabled)', this.panel)?.focus();
      document.addEventListener('click', this.onOutside);
      document.addEventListener('cart:updated', this.onAdded);
      this.onOther = (e) => e.detail !== this && this.close();
      document.addEventListener('quickadd:open', this.onOther);
    }
    close(returnFocus = false) {
      if (this.panel.hidden) return;
      this.panel.hidden = true;
      this.toggle.setAttribute('aria-expanded', 'false');
      this.closest('.card')?.classList.remove('card--choosing');
      document.removeEventListener('click', this.onOutside);
      document.removeEventListener('cart:updated', this.onAdded);
      document.removeEventListener('quickadd:open', this.onOther);
      if (returnFocus) this.toggle.focus();
    }
    disconnectedCallback() {
      this.close();
    }
  }
  customElements.define('quick-add', QuickAdd);

  /* ------------------------------------------------------------ evästeasetukset */
  // Alatunnisteen linkki avaa Shopifyn evästebannerin asetukset. Näytetään vain, kun banneri on käytössä
  // (Shopify lataa window.privacyBanner -olion content_for_headerin kautta, joskus vasta sivun latauksen jälkeen).
  const cookieSettings = $$('[data-cookie-settings]');
  if (cookieSettings.length) {
    let tries = 0;
    const reveal = () => {
      if (window.privacyBanner?.showPreferences) cookieSettings.forEach((el) => (el.hidden = false));
      else if (++tries < 20) setTimeout(reveal, 500);
    };
    reveal();
    cookieSettings.forEach((el) => $('button', el).addEventListener('click', () => window.privacyBanner?.showPreferences()));
  }

  /* ------------------------------------------------------------ tuotekortin värit */
  // Värin kuva kortin pääkuvaksi hiiren tai fokuksen ajaksi (product-card.liquid). Delegointi: toimii myös
  // suodatuksen jälkeen ladatuissa korteissa.
  const cardImage = (el) => $('.card__img', el.closest('.card'));
  const showSwatch = (swatch) => {
    const img = cardImage(swatch);
    if (!img) return;
    if (!('origSrc' in img.dataset)) {
      img.dataset.origSrc = img.getAttribute('src');
      img.dataset.origSrcset = img.getAttribute('srcset') || '';
    }
    img.srcset = swatch.dataset.swatchSrcset;
    img.src = swatch.dataset.swatchSrc;
    $$('.card__swatch', swatch.closest('[data-card-swatches]')).forEach((s) => s.classList.toggle('is-current', s === swatch));
  };
  const resetSwatches = (list) => {
    const img = cardImage(list);
    if (img && 'origSrc' in img.dataset) {
      img.srcset = img.dataset.origSrcset;
      img.src = img.dataset.origSrc;
    }
    $$('.card__swatch', list).forEach((s) => s.classList.remove('is-current'));
  };
  ['pointerover', 'focusin'].forEach((type) =>
    document.addEventListener(type, (e) => {
      const swatch = e.target.closest?.('[data-swatch-src]');
      if (swatch) showSwatch(swatch);
    }),
  );
  ['pointerout', 'focusout'].forEach((type) =>
    document.addEventListener(type, (e) => {
      const list = e.target.closest?.('[data-card-swatches]');
      if (list && !list.contains(e.relatedTarget)) resetSwatches(list);
    }),
  );
  // Väri-suodatin päällä: kortti näyttää valitun sävyn värin kuvan ja linkki avaa tuotteen siinä värissä
  const matchFilteredColors = () => {
    const colors = new URLSearchParams(location.search).getAll('filter.p.m.ridemore.varit');
    if (!colors.length) return;
    $$('[data-card-swatches]').forEach((list) => {
      const swatch = $$('.card__swatch[data-families]', list).find((s) => s.dataset.families.split(',').some((f) => colors.includes(f)));
      if (!swatch) return;
      const link = $('.card__link', list.closest('.card'));
      if (link) link.href = swatch.href;
      if (!swatch.dataset.swatchSrc) return;
      showSwatch(swatch);
      const img = cardImage(swatch);
      img.dataset.origSrc = swatch.dataset.swatchSrc;
      img.dataset.origSrcset = swatch.dataset.swatchSrcset;
    });
  };
  matchFilteredColors();
  document.addEventListener('shopify:section:load', matchFilteredColors);

  /* ------------------------------------------------------------ esiinnousut osiot */
  if ('IntersectionObserver' in window && !reduceMotion.matches) {
    const io = new IntersectionObserver(
      (entries) =>
        entries.forEach((e) => {
          if (e.isIntersecting) {
            e.target.classList.add('is-visible');
            io.unobserve(e.target);
          }
        }),
      { rootMargin: '0px 0px -8% 0px' },
    );
    const observe = (root = document) => $$('.reveal:not(.is-visible)', root).forEach((el) => io.observe(el));
    observe();
    document.addEventListener('shopify:section:load', (e) => observe(e.target));
  } else {
    $$('.reveal').forEach((el) => el.classList.add('is-visible'));
  }
})();
